import styled, { css } from 'styled-components';
import {
	flow,
	FlowPanel,
	PanelTitle,
	Pills,
	Pill,
	ProgressBar,
	SpreadLine,
	Muted,
	Note,
	Small,
	Foot,
	FlowButton,
} from './primitives';
import { parseJobTime } from './ProjectCards';

/**
 * Right column while a job is queued, waiting for a GPU, running, or stopped. The phases follow the progress values
 * the backend writes (src/ai/video_detection.py): <5 starting the machine, 5-19 preparing, 20-69 detecting and
 * tracking, 70-99 result video and report.
 */

export const WAITING_RE = /waiting for a free gpu/i;
const CAPACITY_RE = /insufficientinstancecapacity|no gpu capacity|waiting for a free gpu/i;

const PHASES = [
	{ from: 0, name: 'Starting a GPU machine' },
	{ from: 5, name: 'Preparing the video' },
	{ from: 20, name: 'Finding and tracking animals' },
	{ from: 70, name: 'Making the result video and report' },
];

export const jobPhase = job => {
	if (!job) return 'none';
	if (job.job_status === 'failed') return 'stopped';
	if (job.job_status === 'finished') return 'finished';
	if (WAITING_RE.test(job.message || '')) return 'waiting';
	return 'running';
};

export const stoppedReason = job => {
	const text = `${job?.error_message || ''} ${job?.message || ''}`;
	if (CAPACITY_RE.test(text))
		return 'No GPU machine became free in time. Nothing is wrong with your video.';
	return 'The analysis could not finish. Your video and settings are saved.';
};

const sinceMinutes = iso => {
	if (!iso) return null;
	const d = parseJobTime(iso);
	return Number.isNaN(d.getTime())
		? null
		: Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
};

const span = min => (min < 60 ? `${min} min` : `${Math.floor(min / 60)} h ${min % 60} min`);

/** The waiting state: why, that the user can leave, and what to expect once a machine is free. */
const WaitingInfo = ({ job, expectedMinutes }) => {
	const waited = sinceMinutes(job?.capacity_waiting_since || job?.created_at);
	const maxHours = job?.capacity_max_wait_hours;
	return (
		<>
			<Note>
				<b>All GPU machines are busy right now.</b> Your video is uploaded and your settings
				are saved. The job is in the queue and starts by itself as soon as a machine is
				free.
			</Note>
			<Note $tone="ok">
				<b>You can close this page.</b> The bell at the top of the portal shows when the
				count is ready, and the results appear on this project.
			</Note>
			<SpreadLine>
				<span>Waiting so far</span>
				<span>{waited != null ? span(waited) : '–'}</span>
			</SpreadLine>
			{expectedMinutes && (
				<SpreadLine>
					<span>Once a machine starts</span>
					<span>about {expectedMinutes} min</span>
				</SpreadLine>
			)}
			<Small>
				We try every zone and machine size again every 2 minutes
				{maxHours ? `, for up to ${Math.round(maxHours)} hours` : ''}. How long the wait is
				depends on demand at the cloud provider and cannot be predicted.
			</Small>
		</>
	);
};

const clock = iso => {
	if (!iso) return null;
	const d = parseJobTime(iso);
	return Number.isNaN(d.getTime())
		? null
		: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const Steps = styled.ol`
	list-style: none;
	margin: 0;
	padding: 0;
`;

const Step = styled.li`
	display: grid;
	grid-template-columns: 22px 1fr;
	gap: 0.75rem;
	align-items: start;
	padding: 0.55rem 0;
	font-size: 0.82rem;
	color: ${flow.text3};
	small {
		display: block;
		font-weight: 400;
		color: ${flow.text3};
		font-size: 0.72rem;
		margin-top: 2px;
	}
	${({ $s }) =>
		$s === 'done' &&
		css`
			color: ${flow.text2};
		`}
	${({ $s }) =>
		($s === 'cur' || $s === 'wait') &&
		css`
			color: ${flow.text};
			font-weight: 600;
		`}
`;

const Marker = styled.span`
	width: 14px;
	height: 14px;
	border-radius: 50%;
	margin-top: 2px;
	justify-self: center;
	border: 1.5px solid #3c403c;
	${({ $s, theme }) =>
		$s === 'done' &&
		css`
			background: ${theme.colors.darkGreen};
			border-color: ${theme.colors.darkGreen};
		`}
	${({ $s, theme }) =>
		$s === 'cur' &&
		css`
			border-color: ${theme.colors.green};
			box-shadow: 0 0 0 4px rgba(32, 172, 107, 0.2);
		`}
	${({ $s }) =>
		$s === 'wait' &&
		css`
			border-color: ${flow.warn};
			box-shadow: 0 0 0 4px rgba(224, 160, 48, 0.18);
		`}
`;

const JobProgressPanel = ({ job, settings = [], expectedMinutes, onRetry, retrying }) => {
	const phase = jobPhase(job);
	const pct = Math.max(0, job?.progress ?? 0);
	const started = clock(job?.created_at);
	const jobId = job?.uuid?.slice(0, 8);

	if (phase === 'stopped')
		return (
			<FlowPanel $fill aria-live="polite">
				<PanelTitle>The analysis stopped</PanelTitle>
				<Pills>
					{settings.map(s => (
						<Pill key={s} $variant="ghost">
							{s}
						</Pill>
					))}
				</Pills>
				<Note $tone="error">{stoppedReason(job)}</Note>
				<Muted>
					{onRetry
						? 'Starting again usually works within a few minutes.'
						: 'Line-crossing runs cannot be restarted from this page yet, because the job does not keep its line. Create a new video project to run it again.'}
				</Muted>
				{onRetry && (
					<FlowButton type="button" onClick={onRetry} disabled={retrying}>
						{retrying ? 'Starting…' : 'Try again'}
					</FlowButton>
				)}
				<Foot>
					Job {jobId}
					{started && ` · ${started}`} · support can look this up
				</Foot>
			</FlowPanel>
		);

	const waiting = phase === 'waiting';
	const current = waiting ? 0 : PHASES.reduce((k, p, i) => (pct >= p.from ? i : k), 0);
	return (
		<FlowPanel $fill aria-live="polite">
			<PanelTitle>{waiting ? 'Waiting for a GPU machine' : 'Analysing'}</PanelTitle>
			<Pills>
				{settings.map((s, i) => (
					<Pill key={s} $variant={i ? 'ghost' : undefined}>
						{s}
					</Pill>
				))}
			</Pills>
			<ProgressBar
				value={waiting ? 3 : pct}
				tone={waiting ? 'warn' : undefined}
				label="Analysis progress"
			/>
			<SpreadLine>
				<span>{waiting ? 'Queued' : `${pct} %`}</span>
				<span>
					{waiting
						? 'trying again every 2 min'
						: expectedMinutes
							? `usually about ${expectedMinutes} min in total`
							: ''}
				</span>
			</SpreadLine>
			<Steps>
				<Step $s="done">
					<Marker $s="done" />
					<div>Upload received</div>
				</Step>
				{PHASES.map((p, i) => {
					const s = waiting
						? i === 0
							? 'wait'
							: ''
						: i < current
							? 'done'
							: i === current
								? 'cur'
								: '';
					return (
						<Step key={p.name} $s={s}>
							<Marker $s={s} />
							<div>
								{i === 0 && waiting ? 'Waiting for a free GPU machine' : p.name}
								{s === 'cur' && job?.message && <small>{job.message}</small>}
							</div>
						</Step>
					);
				})}
			</Steps>
			{waiting ? (
				<WaitingInfo job={job} expectedMinutes={expectedMinutes} />
			) : (
				<Muted>
					You can leave this page; the count keeps running and the results appear here.
				</Muted>
			)}
			<Foot>
				{started && `Started ${started} · `}job {jobId}
			</Foot>
		</FlowPanel>
	);
};

export default JobProgressPanel;
